import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/lib/status";
import { LABEL_PRESETS, getPreset, labelLayout, labelPrintCss } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Printer, Download, ArrowLeft, Zap } from "lucide-react";
import { toast } from "sonner";

export default function PatTag() {
  const { id } = useParams();
  const nav = useNavigate();
  const [asset, setAsset] = useState(null);
  const [sizeId, setSizeId] = useState("zebra-57x32");
  const [cw, setCw] = useState(60);
  const [ch, setCh] = useState(30);
  const [pdfBusy, setPdfBusy] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => { api.getAsset(id).then(setAsset).catch(() => nav("/assets")); }, [id, nav]);

  if (!asset) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const tests = asset.pat_tests || [];
  const latest = tests.length ? [...tests].sort((a, b) => (a.date || "").localeCompare(b.date || "")).slice(-1)[0] : null;
  const preset = getPreset(sizeId);
  const dimW = sizeId === "custom" ? Number(cw) : preset.w;
  const dimH = sizeId === "custom" ? Number(ch) : preset.h;
  const sized = dimW > 0 && dimH > 0;
  const L = labelLayout(dimW, dimH);
  const cardStyle = sized ? { width: `${dimW}mm`, height: `${dimH}mm` } : { width: "80mm", minHeight: "40mm" };

  const downloadPdf = async () => {
    const el = cardRef.current;
    if (!el) return;
    setPdfBusy(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const fmt = sized ? [dimW, dimH] : "a4";
      await html2pdf().set({
        margin: 0,
        filename: `PAT_tag_${asset.asset_code || asset.name}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 3, useCORS: true, backgroundColor: "#ffffff", logging: false },
        jsPDF: { unit: "mm", format: fmt, orientation: sized && dimW >= dimH ? "landscape" : "portrait" },
      }).from(el).save();
      toast.success("Tag PDF downloaded");
    } catch { toast.error("PDF export failed"); }
    finally { setPdfBusy(false); }
  };

  return (
    <div className="relative z-10 min-h-screen report-root">
      {sized && <style dangerouslySetInnerHTML={{ __html: labelPrintCss(dimW, dimH) }} />}
      <div className="sticky top-0 z-40 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-background/90 px-4 py-3 backdrop-blur-xl no-print sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => nav(`/assets/${id}`)} className="gap-2"><ArrowLeft className="h-4 w-4" /> Asset</Button>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Select value={sizeId} onValueChange={setSizeId}>
            <SelectTrigger data-testid="pat-label-size" className="h-9 w-56 bg-secondary/40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{LABEL_PRESETS.map((p) => <SelectItem key={p.id} value={p.id} className="text-xs">{p.label}</SelectItem>)}</SelectContent>
          </Select>
          {sizeId === "custom" && (
            <div className="flex items-center gap-1">
              <Input type="number" value={cw} onChange={(e) => setCw(e.target.value)} className="h-9 w-16 bg-secondary/40 text-xs" />
              <span className="text-xs text-muted-foreground">×</span>
              <Input type="number" value={ch} onChange={(e) => setCh(e.target.value)} className="h-9 w-16 bg-secondary/40 text-xs" />
            </div>
          )}
          <Button data-testid="download-pat-tag-btn" onClick={downloadPdf} disabled={pdfBusy} variant="outline" className="gap-2 tap-scale">{pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} <span className="hidden sm:inline">PDF</span></Button>
          <Button data-testid="print-pat-tag-btn" onClick={() => window.print()} className="gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90"><Printer className="h-4 w-4" /> Print</Button>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-8">
        {!latest && <p className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm no-print">No PAT test recorded yet — the tag will show the asset details only.</p>}
        <div className="tag-sheet flex flex-wrap gap-3">
          <div ref={cardRef} style={{ ...cardStyle, padding: `${L.pad}px`, gap: `${L.gap}px` }} className="tag-card print-avoid-break flex rounded-xl border-2 border-border bg-card">
            <div className="shrink-0 rounded-md bg-white" style={{ padding: L.tier === "tiny" ? 2 : 6 }}><QRCodeSVG value={`${window.location.origin}/assets/${id}`} size={L.qr} /></div>
            <div className="flex min-w-0 flex-1 flex-col justify-between">
              <div>
                {L.showAccent && <div className="flex items-center gap-1 font-semibold uppercase tracking-wider text-primary print-accent" style={{ fontSize: L.accent }}><Zap className="h-3 w-3" /> PAT / Electrical test</div>}
                <div className="truncate font-bold leading-tight text-foreground" style={{ fontSize: L.name }}>{asset.name}</div>
                <div className="font-mono font-extrabold text-foreground" style={{ fontSize: L.code }}>{asset.asset_code}</div>
              </div>
              <div className="leading-snug" style={{ fontSize: L.meta }}>
                <div className="flex items-center gap-1.5"><span className="text-muted-foreground">Result</span> {L.tier === "tiny" ? <span className="font-semibold text-foreground">{latest?.result || "Untested"}</span> : <StatusBadge value={latest?.result || "Untested"} />}</div>
                {L.showSecondary && <div><span className="text-muted-foreground">Tested</span> <span className="font-mono">{latest?.date || "—"}</span></div>}
                <div><span className="text-muted-foreground">Next due</span> <span className="font-mono font-bold">{latest?.next_due || "—"}</span></div>
                {L.showSecondary && <div className="truncate"><span className="text-muted-foreground">By</span> {latest?.tested_by || "—"}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
